import { Link } from 'react-router-dom';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-dark-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img src="/logo.png" alt="ZEPHYR AI" className="w-10 h-10 rounded-2xl" />
            <span className="text-xl font-bold">ZEPHYR AI</span>
          </div>
          <div className="hidden md:flex space-x-8">
            <a href="#features" className="text-gray-400 hover:text-white transition">Features</a>
            <a href="#screenshots" className="text-gray-400 hover:text-white transition">Gallery</a>
            <Link to="/support" className="text-gray-400 hover:text-white transition">Support</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition">Terms</Link>
          </div>
          <button className="bg-primary hover:bg-primary/90 px-6 py-2 rounded-full font-medium transition">
            Download
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
                <span className="text-sm text-primary font-medium">AI-Powered Photo Editor</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                Transform Your Photos with{' '}
                <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                  AI Magic
                </span>
              </h1>

              <p className="text-xl text-gray-400 leading-relaxed">
                Professional photo editing powered by artificial intelligence.
                Create stunning visuals with just a tap.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-primary hover:bg-primary/90 px-8 py-4 rounded-full font-semibold text-lg transition transform hover:scale-105">
                  Download for Android
                </button>
                <button className="border border-white/20 hover:bg-white/5 px-8 py-4 rounded-full font-semibold text-lg transition">
                  Learn More
                </button>
              </div>

              <div className="flex items-center space-x-8 pt-4">
                <div>
                  <div className="text-3xl font-bold">500K+</div>
                  <div className="text-gray-400">Downloads</div>
                </div>
                <div className="w-px h-12 bg-white/10"></div>
                <div>
                  <div className="text-3xl font-bold">4.8★</div>
                  <div className="text-gray-400">Rating</div>
                </div>
                <div className="w-px h-12 bg-white/10"></div>
                <div>
                  <div className="text-3xl font-bold">50+</div>
                  <div className="text-gray-400">AI Effects</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative bg-gradient-to-br from-primary/20 to-purple-600/20 rounded-3xl p-1">
                <img
                  src="/images/mVRezaBFem.webp"
                  alt="App Screenshot"
                  className="rounded-3xl aspect-[9/16] object-cover"
                />
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-600/20 rounded-full blur-2xl animate-pulse delay-75"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-dark-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-400">
              Everything you need to create stunning visuals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-dark-700/50 border border-white/10 rounded-2xl p-8 md:hover:border-primary/50 transition-colors">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-purple-600 rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl">{feature.icon}</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="screenshots" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-400">
              Experience the power of AI photo editing
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {screenshots.map((screenshot, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative bg-gradient-to-br from-primary/10 to-purple-600/10 rounded-2xl overflow-hidden aspect-[9/16] border border-white/10 hover:border-primary/50 transition">
                  <img
                    src={screenshot.image}
                    alt={screenshot.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-white font-semibold text-lg">{screenshot.title}</p>
                      <p className="text-white/80 text-sm mt-1">{screenshot.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-primary to-purple-600 rounded-3xl p-12 md:p-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Create Magic?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Join thousands of creators using ZEPHYR AI to transform their photos
            </p>
            <button className="bg-white text-primary hover:bg-gray-100 px-10 py-4 rounded-full font-bold text-lg transition transform hover:scale-105">
              Download Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <img src="/logo.png" alt="ZEPHYR AI" className="w-10 h-10 rounded-2xl" />
                <span className="text-xl font-bold">ZEPHYR AI</span>
              </div>
              <p className="text-gray-400 mb-4">
                Professional AI-powered photo editing for everyone.
              </p>
              <div className="text-gray-400 space-y-2">
                <p>📧 info@zeynsoft.com</p>
                <p>📍 Yenişehir, Mersin, Turkey</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#screenshots" className="hover:text-white transition">Gallery</a></li>
                <li><a href="https://play.google.com/store" className="hover:text-white transition">Download</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/support" className="hover:text-white transition">Support</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition">Terms of Service</Link></li>
                <li><Link to="/account-deletion" className="hover:text-white transition">Delete Account</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-gray-400">
            <p>© {new Date().getFullYear()} ZEPHYR AI by Zeynsoft. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

const features = [
  {
    icon: '✨',
    title: 'AI Magic Effects',
    description: 'Transform your photos with one-tap AI-powered effects and filters'
  },
  {
    icon: '🎨',
    title: 'Professional Tools',
    description: 'Advanced editing tools for precise control over your images'
  },
  {
    icon: '⚡',
    title: 'Lightning Fast',
    description: 'Process photos instantly with our optimized AI engine'
  },
  {
    icon: '🌈',
    title: 'Stunning Filters',
    description: 'Choose from 50+ professional filters and presets'
  },
  {
    icon: '💎',
    title: 'Premium Quality',
    description: 'Export in high resolution without losing quality'
  },
  {
    icon: '📱',
    title: 'Easy to Use',
    description: 'Intuitive interface designed for everyone'
  }
];

const screenshots = [
  {
    image: '/images/pAYwvych.webp',
    alt: 'ZEPHYR AI - AI Tools Selection',
    title: 'AI-Powered Tools',
    description: 'Choose from a variety of AI-powered editing tools'
  },
  {
    image: '/images/dCRsSChLIxsd.webp',
    alt: 'ZEPHYR AI - Photo Editing Interface',
    title: 'Professional Editing',
    description: 'Intuitive interface with powerful editing features'
  },
  {
    image: '/images/jEovAKXhKtX.webp',
    alt: 'ZEPHYR AI - AI Processing Result',
    title: 'Stunning Results',
    description: 'See your photos transform with AI magic'
  }
];
